import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Tags, History, Percent } from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { useSaudiDateFilter } from '../../products/saudi/context/SaudiDateFilterContext';
import { isDateInRange } from '../../products/saudi/utils/dateRangePresets';

const NS = 'modules.article.pages.analysesRapportsPage';

const entryDate = (item) => item.changed_at || item.created_at || item.date;

const unwrapList = (response) => {
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
};

export default function ArticlesAnalysesRapportsPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { range } = useSaudiDateFilter();

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [variantes, setVariantes] = useState([]);
  const [changelog, setChangelog] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsRes, servicesRes, variantesRes, changelogRes] = await Promise.all([
        localApiAdapter.products.getProducts().catch(() => []),
        localApiAdapter.get('/services').catch(() => []),
        localApiAdapter.get('/api/articles/variantes').catch(() => []),
        localApiAdapter.get('/api/articles/changelog').catch(() => []),
      ]);
      setProducts(unwrapList(productsRes));
      setServices(unwrapList(servicesRes));
      setVariantes(unwrapList(variantesRes));
      setChangelog(unwrapList(changelogRes));
    } catch (error) {
      console.error('Error loading articles analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const catalogValue = useMemo(
    () =>
      products.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
      + services.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0),
    [products, services],
  );

  const isActive = useCallback(
    (item) => item.status === 'active' || item.status === 'actif' || item.active === true,
    [],
  );

  const activeProducts = useMemo(
    () => products.filter(isActive).length,
    [products, isActive],
  );

  const activeServices = useMemo(
    () => services.filter(isActive).length,
    [services, isActive],
  );

  const catalogTotal = products.length + services.length;

  const distinctCategories = useMemo(() => {
    const cats = new Set();
    products.forEach((item) => {
      cats.add(item.category || item.category_name || p('uncategorized'));
    });
    services.forEach((item) => {
      cats.add(item.category || item.category_name || p('uncategorized'));
    });
    return cats.size;
  }, [products, services, t]);

  const periodChangelog = useMemo(
    () => changelog.filter((entry) => isDateInRange(entryDate(entry), range)),
    [changelog, range],
  );

  const periodNewItems = useMemo(() => {
    const inPeriod = (item) => isDateInRange(item.created_at, range);
    return products.filter(inPeriod).length + services.filter(inPeriod).length;
  }, [products, services, range]);

  const productsByCategory = useMemo(() => {
    const map = {};
    products.forEach((item) => {
      const cat = item.category || item.category_name || p('uncategorized');
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [products, t]);

  const topCategory = productsByCategory[0];

  const activeRatioPct = useMemo(() => {
    if (catalogTotal === 0) return 0;
    return Math.round(((activeProducts + activeServices) / catalogTotal) * 100);
  }, [catalogTotal, activeProducts, activeServices]);

  const changelogByEntity = useMemo(() => {
    const map = {};
    periodChangelog.forEach((entry) => {
      const key = entry.entity_type || 'other';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([key, value]) => ({
        label: p(`entity_${key}`, { defaultValue: key }),
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [periodChangelog, t]);

  const insights = useMemo(
    () => [
      {
        label: p('insight_catalog_growth'),
        value: p('insight_catalog_growth_value', { count: periodNewItems }),
        hint: p('insight_catalog_growth_hint'),
      },
      {
        label: p('insight_active_ratio'),
        value: p('insight_active_ratio_value', { percent: activeRatioPct }),
        hint: p('insight_active_ratio_hint', {
          active: activeProducts + activeServices,
          total: catalogTotal,
        }),
      },
      {
        label: p('insight_top_category'),
        value: topCategory
          ? p('insight_top_category_value', { name: topCategory.label, count: topCategory.value })
          : p('uncategorized'),
        hint: p('insight_top_category_hint'),
      },
      {
        label: p('insight_period_changes'),
        value: String(periodChangelog.length),
        hint: p('insight_period_changes_hint'),
      },
    ],
    [periodNewItems, activeRatioPct, activeProducts, activeServices, catalogTotal, topCategory, periodChangelog.length, t],
  );

  const stats = useMemo(
    () => [
      { label: p('kpi_catalog_total'), value: catalogTotal, icon: Layers, variant: 'forest' },
      { label: p('kpi_distinct_categories'), value: distinctCategories, icon: Tags, variant: 'copper' },
      { label: p('kpi_period_changes'), value: periodChangelog.length, icon: History, variant: 'forest' },
      {
        label: p('kpi_active_ratio'),
        value: p('kpi_active_ratio_value', { percent: activeRatioPct }),
        icon: Percent,
        variant: 'copper',
      },
    ],
    [catalogTotal, distinctCategories, periodChangelog.length, activeRatioPct, t],
  );

  const charts = useMemo(() => {
    const catalogChart = {
      title: p('chart_catalog_title'),
      subtitle: p('chart_catalog_subtitle'),
      variant: 'pipeline',
      rows: [
        { label: p('chart_products'), value: products.length },
        { label: p('chart_services'), value: services.length },
        { label: p('chart_variants'), value: variantes.length },
      ],
    };
    const statusChart = {
      title: p('chart_status_title'),
      subtitle: p('chart_status_subtitle'),
      variant: 'status',
      active: activeProducts + activeServices,
      inactive: Math.max(0, catalogTotal - activeProducts - activeServices),
    };
    const categoryChart = {
      title: p('chart_category_title'),
      subtitle: p('chart_category_subtitle'),
      variant: 'ranked',
      rows: productsByCategory.length
        ? productsByCategory
        : [{ label: p('uncategorized'), value: 0 }],
    };
    return [catalogChart, statusChart, categoryChart];
  }, [
    products.length,
    services.length,
    variantes.length,
    productsByCategory,
    activeProducts,
    activeServices,
    catalogTotal,
    t,
  ]);

  const table = useMemo(
    () => ({
      title: p('report_catalog_title'),
      columns: [
        { key: 'section', label: p('col_section') },
        { key: 'metric', label: t('common.indicator', { defaultValue: 'المؤشر' }) },
        { key: 'value', label: t('common.value', { defaultValue: 'القيمة' }) },
        { key: 'share', label: p('col_share') },
      ],
      rows: [
        {
          section: p('section_catalog'),
          metric: p('kpi_catalog_total'),
          value: String(catalogTotal),
          share: '100%',
        },
        {
          section: p('section_catalog'),
          metric: p('row_products'),
          value: String(products.length),
          share: catalogTotal ? `${Math.round((products.length / catalogTotal) * 100)}%` : '—',
        },
        {
          section: p('section_catalog'),
          metric: p('row_services'),
          value: String(services.length),
          share: catalogTotal ? `${Math.round((services.length / catalogTotal) * 100)}%` : '—',
        },
        {
          section: p('section_catalog'),
          metric: p('row_variants'),
          value: String(variantes.length),
          share: '—',
        },
        {
          section: p('section_catalog'),
          metric: p('row_catalog_value'),
          value: formatBrandMoney(catalogValue),
          share: '—',
        },
        {
          section: p('section_status'),
          metric: p('status_active'),
          value: String(activeProducts + activeServices),
          share: catalogTotal ? `${activeRatioPct}%` : '—',
        },
        {
          section: p('section_status'),
          metric: p('status_inactive'),
          value: String(Math.max(0, catalogTotal - activeProducts - activeServices)),
          share: catalogTotal ? `${100 - activeRatioPct}%` : '—',
        },
        {
          section: p('section_period'),
          metric: p('kpi_period_changes'),
          value: String(periodChangelog.length),
          share: changelog.length ? `${Math.round((periodChangelog.length / changelog.length) * 100)}%` : '—',
        },
        {
          section: p('section_period'),
          metric: p('row_new_items'),
          value: String(periodNewItems),
          share: '—',
        },
        ...productsByCategory.map((row) => ({
          section: p('section_categories'),
          metric: row.label,
          value: String(row.value),
          share: products.length ? `${Math.round((row.value / products.length) * 100)}%` : '—',
        })),
        ...changelogByEntity.map((row) => ({
          section: p('section_changelog'),
          metric: row.label,
          value: String(row.value),
          share: periodChangelog.length
            ? `${Math.round((row.value / periodChangelog.length) * 100)}%`
            : '—',
        })),
      ],
    }),
    [
      catalogTotal,
      products.length,
      services.length,
      variantes.length,
      catalogValue,
      activeProducts,
      activeServices,
      activeRatioPct,
      periodChangelog.length,
      periodNewItems,
      changelog.length,
      productsByCategory,
      changelogByEntity,
      t,
    ],
  );

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={loadData}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="articles_analytics"
      emptyLabel={p('no_data')}
    />
  );
}
